import React, { useEffect, useMemo, useState } from 'react';
import { loadSubscriptionConfig } from '../lib/subscriptionConfig.js';
import { activateBusiness } from '../lib/businesses.js';
import './ActivationModal.css';

// ============================================================
// ActivationModal — Session 6a (Phase 2).
//
// Admin activates a business by picking a tier (₹199 / ₹499 / ₹899),
// entering a payment reference (UPI ref / bank txn ID), and confirming.
// Valid-till is auto-computed from today + tier.durationMonths. Admin
// can optionally override the valid-till date for exceptions (trial
// extensions, promotions, etc.) — the override is recorded separately
// on the doc for audit.
//
// LAYOUT (matches prototype):
//   - Header: title + biz name + current activation state ("currently
//     inactive" / "currently active until DD MMM YYYY")
//   - Choose validity: 3 tier buttons
//   - Payment received date: read-only "today" (Q_C decision: no
//     back-dating; if admin needs to adjust valid-till, use the
//     override instead)
//   - Valid till (auto-calculated): computed live from selected tier
//   - Need to override Valid Till: collapsible with a date input
//   - Payment reference: text input (required)
//   - Note (optional): textarea
//   - Cancel + Confirm activation buttons
//
// STATE FLOW:
//   Mount → fetch config (1 read) → user picks tier + fills fields →
//   Confirm → activateBusiness (1 write) → parent closes modal + refetches
//
// COST: 1 read (config) + 1 write (activation) per confirmed activation.
// Cancel = 1 read total.
// ============================================================

// Format a Date for display in the drawer and modal header.
function fmtDate(d) {
  if (!d) return '';
  if (typeof d?.toDate === 'function') d = d.toDate();
  else if (typeof d?.seconds === 'number') d = new Date(d.seconds * 1000);
  else if (typeof d === 'string') d = new Date(d);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Format a Date as YYYY-MM-DD for <input type="date"> value.
function toDateInputValue(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Add `months` months to a Date, cloning to avoid mutation. Handles month
// rollover naturally via setMonth (Jan 31 + 1 month = Mar 3, matching most
// SaaS billing conventions where "1 month" means "same calendar day
// next month, rolling over if that date doesn't exist").
function addMonths(date, months) {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

// Compute the current activation state for the header sub-label. Reflects
// whichever is TRUE right now: paid activation, trial, or neither.
function currentActivationLabel(biz) {
  const now = new Date();

  // Paid activation window
  const validTill = biz.activationValidTill?.toDate?.() || null;
  if (biz.activated && validTill && validTill > now) {
    return `currently active until ${fmtDate(validTill)}`;
  }

  // Trial window
  const trialStart = biz.trialStartedAt?.toDate?.() || null;
  const trialDays = typeof biz.trialDays === 'number' ? biz.trialDays : 0;
  if (trialStart && trialDays > 0) {
    const trialEnd = new Date(trialStart.getTime());
    trialEnd.setDate(trialEnd.getDate() + trialDays);
    if (trialEnd > now) {
      return `on trial until ${fmtDate(trialEnd)}`;
    }
  }

  return 'currently inactive';
}

export default function ActivationModal({ biz, adminUid, onClose, onSuccess }) {
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const [configIsFallback, setConfigIsFallback] = useState(false);

  const [selectedTierId, setSelectedTierId] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideDate, setOverrideDate] = useState(''); // YYYY-MM-DD or ''
  const [paymentRef, setPaymentRef] = useState('');
  const [note, setNote] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Load subscription config once on mount. If it's slow / errors we
  // still let admin pick a tier from the fallback constants so the flow
  // isn't blocked entirely.
  useEffect(() => {
    let cancelled = false;
    loadSubscriptionConfig()
      .then((res) => {
        if (cancelled) return;
        setConfig(res);
        setConfigIsFallback(res.isFallback);
        // Default to the first tier so the modal has a valid selection
        // out of the gate — matches prototype which pre-selects ₹199.
        if (res.tiers && res.tiers.length > 0) {
          setSelectedTierId(res.tiers[0].id);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[ActivationModal] config load failed:', err);
        setConfigError(err?.message || 'Failed to load tier config');
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const selectedTier = useMemo(() => {
    if (!config?.tiers || !selectedTierId) return null;
    return config.tiers.find((t) => t.id === selectedTierId) || null;
  }, [config, selectedTierId]);

  // Auto-computed valid till = today + selectedTier.durationMonths.
  const autoValidTill = useMemo(() => {
    if (!selectedTier) return null;
    return addMonths(new Date(), selectedTier.durationMonths);
  }, [selectedTier]);

  // Effective valid till = override if admin used it and it's valid,
  // else the auto-computed one.
  const effectiveValidTill = useMemo(() => {
    if (overrideOpen && overrideDate) {
      const d = new Date(overrideDate);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return autoValidTill;
  }, [overrideOpen, overrideDate, autoValidTill]);

  const canConfirm = !submitting
    && !!selectedTier
    && !!effectiveValidTill
    && paymentRef.trim().length > 0;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const overrideValue = overrideOpen && overrideDate
        ? new Date(overrideDate)
        : null;
      await activateBusiness({
        businessId: biz.id,
        adminUid,
        tier: selectedTier,
        validTill: autoValidTill,
        overrideValidTill: overrideValue,
        paymentRef,
        note,
      });
      // Success — let the parent refetch + close the modal.
      onSuccess?.();
    } catch (err) {
      console.error('[ActivationModal] activation failed:', err);
      setSubmitError(err?.message || 'Activation failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Backdrop click closes UNLESS we're mid-submit — don't want a stray
  // click cancelling an in-flight write.
  const handleBackdropClick = () => {
    if (submitting) return;
    onClose?.();
  };

  return (
    <div className="sa-activation-backdrop" onClick={handleBackdropClick}>
      <div
        className="sa-activation-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="sa-activation-header">
          <div className="sa-activation-header-text">
            <div className="sa-activation-title">Activate business</div>
            <div className="sa-activation-sub">
              {biz.name || '—'} · {currentActivationLabel(biz)}
            </div>
          </div>
          <button
            className="sa-activation-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="sa-activation-body">
          {configLoading && (
            <div className="sa-activation-loading">Loading tier pricing…</div>
          )}

          {configError && (
            <div className="sa-error-banner">
              <b>Couldn't load tier pricing.</b> {configError}
            </div>
          )}

          {config && (
            <>
              {configIsFallback && (
                <div className="sa-activation-hint">
                  Using default pricing — <code>/config/subscription</code> doc
                  not found in Firestore. Create it in Firebase Console to
                  customize.
                </div>
              )}

              {/* Tier selection */}
              <div className="sa-activation-section">
                <div className="sa-activation-label">Choose validity</div>
                <div className="sa-activation-tiers">
                  {config.tiers.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`sa-activation-tier ${selectedTierId === t.id ? 'active' : ''}`}
                      onClick={() => setSelectedTierId(t.id)}
                      disabled={submitting}
                    >
                      <div className="sa-activation-tier-amount">₹{t.amount}</div>
                      <div className="sa-activation-tier-label">{t.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Valid till (auto-calculated) */}
              <div className="sa-activation-section">
                <div className="sa-activation-label">Valid till (auto-calculated)</div>
                <div className="sa-activation-valid-till">
                  {autoValidTill ? `${fmtDate(autoValidTill)} (auto)` : '—'}
                </div>
                <div className="sa-activation-help">
                  Calculated as today + {selectedTier?.durationMonths ?? '—'} month{selectedTier?.durationMonths === 1 ? '' : 's'}.
                  Payment received date is today — activatedAt on the record.
                </div>
              </div>

              {/* Override collapsible */}
              <div className="sa-activation-override-block">
                <button
                  type="button"
                  className="sa-activation-override-toggle"
                  onClick={() => setOverrideOpen((v) => !v)}
                  disabled={submitting}
                >
                  <span>
                    Need to override Valid Till?{' '}
                    <span className="sa-activation-override-hint">
                      (use for exceptions like trial extensions)
                    </span>
                  </span>
                  <span className="sa-activation-override-caret">
                    {overrideOpen ? '−' : '+'}
                  </span>
                </button>

                {overrideOpen && (
                  <div className="sa-activation-override-body">
                    <label className="sa-activation-label">
                      Override Valid Till date
                    </label>
                    <input
                      type="date"
                      className="sa-activation-input"
                      value={overrideDate}
                      onChange={(e) => setOverrideDate(e.target.value)}
                      disabled={submitting}
                    />
                    <div className="sa-activation-help">
                      This will override the auto-calculated date. Both the
                      original and overridden dates are recorded on the
                      business doc for the activity log.
                    </div>
                  </div>
                )}
              </div>

              {/* Payment reference */}
              <div className="sa-activation-section">
                <div className="sa-activation-label">Payment reference</div>
                <input
                  type="text"
                  className="sa-activation-input"
                  placeholder="UPI ref / bank transaction ID"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  disabled={submitting}
                />
                <div className="sa-activation-help">
                  Used internally to track which offline payment activated
                  this business. Not shown to the business owner.
                </div>
              </div>

              {/* Note */}
              <div className="sa-activation-section">
                <div className="sa-activation-label">Note (optional)</div>
                <textarea
                  className="sa-activation-textarea"
                  placeholder="e.g. Paid via UPI on 25-May. Receipt attached on WhatsApp."
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {submitError && (
                <div className="sa-error-banner">
                  <b>Activation failed.</b> {submitError}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="sa-activation-footer">
          <button
            type="button"
            className="sa-btn sa-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sa-btn sa-btn-primary"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {submitting ? 'Activating…' : 'Confirm activation'}
          </button>
        </div>
      </div>
    </div>
  );
}
